import React from 'react'
import styled, { css } from 'styled-components'
import Section from '../Section'
import Header, { Title } from '../Header'
import Separator from '../Separator'
import Flex, { FlexProps } from 'components/Box'
import { SymbolLink, SymbolUnavailable } from '../../../Symbol'
import {
  GetArtboardInDocumentWithUuidQuery,
  useGetDetailViewArtboardQuery,
} from 'gqlTypes'
import { ReactComponent as ChevronRight } from 'icons/chevron-horizontal-12.svg'
import { truncateStyles, TruncateWithTooltip } from 'components/Truncate'
import BaseImage from 'components/Image'
import { getArtboardThumbnail } from 'modules/shares/utils/getArtboardThumbnail'
import { useVersioning } from 'modules/versioning'

const SymbolDetail = styled(Flex)<FlexProps & SymbolBoxProps>(
  ({ theme, isClickable }) => css`
    font-size: ${theme.fontSizes.smaller};
    color: ${theme.colors.text.dark.secondary};
    background: ${theme.colors.white};
    border: 1px solid
      ${isClickable
        ? theme.colors.border.dark.extraHigh
        : theme.colors.border.dark.base};
    border-radius: 4px;
    a {
      color: ${theme.colors.text.dark.secondary};
    }
    ${truncateStyles};
  `
)

interface SymbolBoxProps extends FlexProps {
  isClickable?: boolean
}

const SymbolBox = styled(SymbolDetail)<SymbolBoxProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${({ theme }) => theme.colors.text.dark.tertiary};
  cursor: ${props => (props.isClickable ? 'pointer' : 'auto')};
`

const SymbolContainer = styled(Flex)<FlexProps>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  color: ${({ theme }) => theme.colors.text.dark.secondary};
  ${truncateStyles};
`

const SymbolPath = styled.span`
  color: ${({ theme }) => theme.colors.text.dark.tertiary};
`

const SymbolThumbnail = styled(BaseImage)`
  display: flex;
  justify-content: center;

  img {
    max-width: 32px;
    max-height: 32px;
  }
`

type SymbolArtboard = OmitSafe<
  NonNullable<GetArtboardInDocumentWithUuidQuery['artboardInDocumentWithUuid']>,
  '__typename'
>

interface SymbolProps {
  name: string
  symbolArtboard: SymbolArtboard | null
  shareIdentifier: string
  path?: string
  isForeign?: boolean
  foreignVersionShortId?: string
  permanentArtboardShortId?: string
}

const SymbolInSidebar: React.FC<SymbolProps> = ({
  name,
  symbolArtboard,
  shareIdentifier,
  path,
  isForeign,
  foreignVersionShortId,
  permanentArtboardShortId,
}) => {
  const { versionShortId } = useVersioning()

  const { data } = useGetDetailViewArtboardQuery({
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.permanentArtboardShortId !== curr?.permanentArtboardShortId,
    // To account for artboard updates we're setting a cache-and-network
    // policy for artboard queries without an explicit version.
    fetchPolicy: versionShortId ? 'cache-first' : 'cache-and-network',
    variables: {
      permanentArtboardShortId: symbolArtboard?.permanentArtboardShortId ?? '',
      documentVersionShortId: foreignVersionShortId ?? versionShortId,
      shareIdentifier,
    },
    skip:
      !versionShortId ||
      !shareIdentifier ||
      !symbolArtboard?.permanentArtboardShortId,
  })

  const symbolThumbnail = data?.artboard
    ? getArtboardThumbnail(data.artboard, 'S')
    : null

  const isSymbolText =
    !symbolArtboard?.isSymbolMaster ||
    !symbolArtboard?.permanentArtboardShortId ||
    // if we're at the last level of the symbol tree, we don't want to show the link to nowhere
    permanentArtboardShortId === symbolArtboard?.permanentArtboardShortId

  const symbolDetail = (
    <SymbolBox
      padding={3}
      marginRight={7}
      justifyContent="space-between"
      isClickable={!isSymbolText}>
      {symbolThumbnail && (
        <Flex width="32px" marginRight={2}>
          <SymbolThumbnail src={symbolThumbnail} alt="thumbnail" />
        </Flex>
      )}
      <SymbolContainer
        marginRight={2}
        data-testid="inspector-sidebar-symbol-info">
        <TruncateWithTooltip>{name}</TruncateWithTooltip>
        <SymbolPath data-testid="inspector-sidebar-symbol-path">
          <TruncateWithTooltip>{path} </TruncateWithTooltip>
        </SymbolPath>
      </SymbolContainer>
      {!isSymbolText ? <ChevronRight height="17px" /> : null}
    </SymbolBox>
  )

  return (
    <>
      <Separator />
      <Section data-testid="inspector-sidebar-symbol">
        <Header>
          <Title>Symbol</Title>
        </Header>
        {isSymbolText ? (
          <SymbolUnavailable>{symbolDetail}</SymbolUnavailable>
        ) : (
          <SymbolLink
            isForeign={!!isForeign}
            isSamePermanentArtboardShortId={
              permanentArtboardShortId ===
              symbolArtboard?.permanentArtboardShortId
            }
            symbolArtboard={symbolArtboard}
            shareIdentifier={shareIdentifier}>
            {symbolDetail}
          </SymbolLink>
        )}
      </Section>
    </>
  )
}

export { SymbolInSidebar }
